import React, { useState, useMemo, useEffect } from 'react';
import { useCRM } from '../../context/CRMContext';
import { useCRMData } from '../../context/CRMDataContext';
import SafeIcon from '../../common/SafeIcon';
import { getActivityColor } from '../../utils/helpers';
import ActivityConflictWarning from '../common/ActivityConflictWarning';
import { datetimeLocalToISOString, toDatetimeLocalValue } from '../../utils/dateTime';

const HIDDEN_ACTIVITY_STATUSES = new Set([
  'Gjennomført',
  'Arkivert',
  'Slettet',
  'Deleted'
]);

const isActiveActivity = (activity) => (
  !HIDDEN_ACTIVITY_STATUSES.has(activity?.status)
);

const getDealCompanyName = (deal, companies = []) => {
  const companyId = deal?.company_id || deal?.companyId;

  return companies.find(company => (
    company?.company_id === companyId || company?.id === companyId
  ))?.name || '';
};

const getDealDisplayLabel = (deal, companies = []) => {
  const dealTitle = deal?.title || deal?.name || 'Uten navn';
  const companyName = getDealCompanyName(deal, companies);

  return companyName ? `${companyName} – ${dealTitle}` : dealTitle;
};

const splitDatetimeLocal = (value) => {
  if (!value) return { date: '', time: '' };

  const [date = '', time = ''] = value.split('T');
  return {
    date,
    time: time.slice(0, 5)
  };
};

const combineDatetimeLocal = (date, time) => {
  if (!date || !time) return '';
  return `${date}T${time}`;
};

const ActivityForm = ({ activity, onClose }) => {
  const { users, companies, contacts, deals } = useCRM();
  const { addActivity, updateActivity, activities } = useCRMData();

  const [activeTab, setActiveTab] = useState(
    activity ? (activity.type === 'Oppgave' ? 'task' : (['Samtale', 'Møte', 'Online møte', 'E-post'].includes(activity.type) ? 'meeting' : 'other')) : 'task'
  );

  const [formData, setFormData] = useState(activity ? {
    ...activity,
    date: toDatetimeLocalValue(activity.date),
    end_date: toDatetimeLocalValue(activity.end_date)
  } : {
    type: 'Oppgave',
    user_id: users[0]?.user_id || '',
    company_id: '',
    contact_id: '',
    deal_id: '',
    project_id: '',
    date: toDatetimeLocalValue(new Date(new Date().getTime() + 60 * 60 * 1000)),
    end_date: toDatetimeLocalValue(new Date(new Date().getTime() + 120 * 60 * 1000)),
    title: '',
    description: '',
    internal_notes: '',
    location: '',
    status: 'Planlagt',
    is_online_meeting: false,
    reminders: []
  });

  const initialStartDateTime = splitDatetimeLocal(formData.date);
  const initialEndDateTime = splitDatetimeLocal(formData.end_date);
  const [startDate, setStartDate] = useState(initialStartDateTime.date);
  const [startTime, setStartTime] = useState(initialStartDateTime.time);
  const [endDate, setEndDate] = useState(initialEndDateTime.date || initialStartDateTime.date);
  const [endTime, setEndTime] = useState(initialEndDateTime.time);
  const [conflictAcknowledged, setConflictAcknowledged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!activity) return;

    const nextDate = toDatetimeLocalValue(activity.date);
    const nextEndDate = toDatetimeLocalValue(activity.end_date);
    const nextStart = splitDatetimeLocal(nextDate);
    const nextEnd = splitDatetimeLocal(nextEndDate);
    const resolvedNextEndDate = nextEnd.date || nextStart.date;

    setFormData(prev => ({
      ...prev,
      ...activity,
      date: nextDate,
      end_date: nextEndDate
    }));
    setStartDate(nextStart.date);
    setStartTime(nextStart.time);
    setEndDate(resolvedNextEndDate);
    setEndTime(nextEnd.time);
  }, [activity?.activity_id, activity?.date, activity?.end_date]);

  const reminderOptions = [
    { label: '5 min', value: 5 },
    { label: '15 min', value: 15 },
    { label: '1 t', value: 60 },
    { label: '2 t', value: 120 },
    { label: '1 dag', value: 1440 }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'Samtale': return '📞';
      case 'Online møte': return '🎥';
      case 'E-post': return '📧';
      case 'Møte': return '👥';
      case 'Oppgave': return '✅';
      default: return '📅';
    }
  };

  const toggleReminder = (minutes) => {
    setFormData(prev => {
      const exists = prev.reminders?.includes(minutes);
      const newReminders = exists
        ? prev.reminders.filter(r => r !== minutes)
        : [...(prev.reminders || []), minutes];
      return { ...prev, reminders: newReminders };
    });
  };

  const handleDateTimeChange = (field, value) => {
    if (field === 'startDate') {
      const resolvedEndDate = endDate || value;
      setStartDate(value);
      if (!endDate) setEndDate(value);
      setFormData(prev => ({
        ...prev,
        date: combineDatetimeLocal(value, startTime),
        end_date: combineDatetimeLocal(resolvedEndDate, endTime)
      }));
      return;
    }

    if (field === 'startTime') {
      setStartTime(value);
      setFormData(prev => ({
        ...prev,
        date: combineDatetimeLocal(startDate, value)
      }));
      return;
    }

    if (field === 'endDate') {
      setEndDate(value);
      setFormData(prev => ({
        ...prev,
        end_date: combineDatetimeLocal(value || startDate, endTime)
      }));
      return;
    }

    const resolvedEndDate = endDate || startDate;
    setEndTime(value);
    if (!endDate) setEndDate(resolvedEndDate);
    setFormData(prev => ({
      ...prev,
      end_date: combineDatetimeLocal(resolvedEndDate, value)
    }));
  };

  const activeActivities = useMemo(
    () => (activities || []).filter(isActiveActivity),
    [activities]
  );

  const sameDayActivities = useMemo(() => {
    if (!formData.date) return [];
    const selectedDate = new Date(formData.date).toDateString();
    return activeActivities
      .filter(a => new Date(a.date).toDateString() === selectedDate && a.activity_id !== (activity?.activity_id))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [formData.date, activeActivities, activity]);

  const conflictingActivities = useMemo(() => {
    if (!formData.date) return [];

    const start = new Date(formData.date).getTime();
    const end = formData.end_date
      ? new Date(formData.end_date).getTime()
      : start + 60 * 60 * 1000;

    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return [];

    return activeActivities
      .filter(existing => existing.activity_id !== activity?.activity_id)
      .filter(existing => {
        const existingStart = new Date(existing.date).getTime();
        const existingEnd = existing.end_date
          ? new Date(existing.end_date).getTime()
          : existingStart + 60 * 60 * 1000;

        if (Number.isNaN(existingStart) || Number.isNaN(existingEnd)) return false;

        return start < existingEnd && end > existingStart;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [formData.date, formData.end_date, activeActivities, activity]);

  useEffect(() => {
    setConflictAcknowledged(false);
  }, [formData.date, formData.end_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');

    if (conflictingActivities.length > 0 && !conflictAcknowledged) {
      return;
    }

    let finalType = formData.type;
    
    if (activeTab === 'task') finalType = 'Oppgave';
    else if (activeTab === 'meeting') {
      if (formData.is_online_meeting) finalType = 'Online møte';
      else finalType = formData.type || 'Møte';
    } else {
      finalType = 'Annet';
    }

    const localStart = combineDatetimeLocal(startDate, startTime);
    const resolvedEndDate = endDate || startDate;
    const localEnd = endTime
      ? combineDatetimeLocal(resolvedEndDate, endTime)
      : '';

    const submission = {
      ...formData,
      type: finalType,
      status: formData.status === 'Gjennomført' ? 'Gjennomført' : 'Planlagt',
      date: datetimeLocalToISOString(localStart),
      end_date: datetimeLocalToISOString(localEnd)
    };

    console.log('[ActivityForm submission]', submission);

    setIsSaving(true);

    try {
      const result = activity?.activity_id
        ? await updateActivity(activity.activity_id, submission)
        : await addActivity(submission);

      if (!result?.success) {
        setSaveError(result?.error?.message || 'Aktiviteten kunne ikke lagres.');
        return;
      }

      onClose();
    } catch (error) {
      console.error('[ActivityForm] save failed', error);
      setSaveError(error.message || 'Aktiviteten kunne ikke lagres.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const currentColors = getActivityColor(formData.type);

  return (
    <div className="flex flex-col lg:flex-row h-full min-h-[600px] bg-slate-50 rounded-3xl overflow-hidden shadow-inner">
      
      {/* Left Column: Form */}
      <div className="flex-1 p-6 md:p-8 border-r border-slate-200 flex flex-col bg-white">
        
        {/* Tydeligere tabs */}
        <div className="flex gap-2 bg-slate-100 p-1.5 rounded-xl border border-slate-200 mb-8 shadow-sm">
          {[
            { id: 'task', label: 'Oppgave', icon: '✅', type: 'Oppgave' },
            { id: 'meeting', label: 'Møte/Dialog', icon: '👥', type: 'Møte' },
            { id: 'other', label: 'Annet', icon: '📅', type: 'Annet' }
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
                setFormData({ ...formData, type: tab.type });
              }}
              className={`flex-1 flex items-center justify-center py-2.5 text-xs font-bold uppercase tracking-widest rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-white shadow-sm text-blue-700 border border-slate-200'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        <form id="activity-form" onSubmit={handleSubmit} className="space-y-6 flex-1 overflow-y-auto custom-scrollbar pr-2">
          
          {/* Tydelig overskriftsfelt */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">
              Hva skal gjøres? <span className="text-red-500">*</span>
            </label>
            <input
              required
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="F.eks: Ringe for å følge opp tilbud"
              className="w-full text-base font-bold text-slate-900 border border-slate-300 rounded-xl px-4 py-3 bg-white placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none shadow-sm transition-all"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs font-black text-slate-500 uppercase tracking-widest ml-1">
                <SafeIcon icon="📋" className="w-3 h-3 text-slate-400" /> Type Aktivitet
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none shadow-sm transition-all"
              >
                <option value="Oppgave">Oppgave</option>
                <option value="Møte">Fysisk Møte</option>
                <option value="Online møte">Online Møte</option>
                <option value="Samtale">Telefonsamtale</option>
                <option value="E-post">E-post</option>
                <option value="Annet">Annet</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs font-black text-slate-500 uppercase tracking-widest ml-1">
                <SafeIcon icon="🔔" className="w-3 h-3 text-slate-400" /> Påminnelser
              </label>
              <div className="flex flex-wrap gap-2">
                {reminderOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleReminder(opt.value)}
                    className={`px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all border ${
                      formData.reminders?.includes(opt.value)
                        ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Gruppert Tids-seksjon med ramme */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <SafeIcon icon="🕒" className="w-4 h-4 text-slate-400" /> Tidspunkt
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3 min-w-0">
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase ml-1">Startdato</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleDateTimeChange('startDate', e.target.value)}
                    className="w-full min-w-0 bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-all"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase ml-1">Starttid</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => handleDateTimeChange('startTime', e.target.value)}
                    className="w-full min-w-0 bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-all"
                  />
                </div>
              </div>
              <div className="space-y-3 min-w-0">
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase ml-1">Sluttdato</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => handleDateTimeChange('endDate', e.target.value)}
                    className="w-full min-w-0 bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-all"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase ml-1">Sluttid</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => handleDateTimeChange('endTime', e.target.value)}
                    className="w-full min-w-0 bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-all"
                  />
                </div>
              </div>
            </div>

            <ActivityConflictWarning
              conflicts={conflictingActivities}
              acknowledged={conflictAcknowledged}
              onAcknowledge={() => setConflictAcknowledged(true)}
            />

            {saveError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                {saveError}
              </div>
            )}
          </div>

          {/* Gruppert Entitet-seksjon med ramme */}
          <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 space-y-4">
            <h4 className="text-xs font-black text-blue-800 uppercase tracking-widest flex items-center gap-2">
              <SafeIcon icon="🔗" className="w-4 h-4 text-blue-400" /> Knytt til
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <select
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-all"
              >
                <option value="">-- Velg selskap --</option>
                {(companies || []).map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
              </select>
              <select
                name="deal_id"
                value={formData.deal_id}
                onChange={handleChange}
                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-all"
              >
                <option value="">-- Velg deal --</option>
                {(deals || []).map(d => <option key={d.deal_id} value={d.deal_id}>{getDealDisplayLabel(d, companies)}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-xs font-black text-slate-500 uppercase tracking-widest ml-1">
              <SafeIcon icon="📝" className="w-3 h-3 text-slate-400" /> Beskrivelse / Notater
            </label>
            <textarea
              name="description"
              rows="3"
              value={formData.description}
              onChange={handleChange}
              placeholder="Skriv inn notater, agenda eller detaljer her..."
              className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none shadow-sm transition-all"
            />
          </div>

        </form>

        {/* Footer Area */}
        <div className="mt-6 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          
          <label className="flex items-center gap-3 group cursor-pointer w-full sm:w-auto p-2 hover:bg-slate-50 rounded-xl transition-colors">
            <div className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center transition-all ${
              formData.status === 'Gjennomført' 
                ? 'bg-emerald-500 border-emerald-500 text-white shadow-md' 
                : 'bg-white border-slate-300 group-hover:border-emerald-400 text-transparent shadow-sm'
            }`}>
              <SafeIcon icon="✓" className="w-5 h-5" />
            </div>
            <input
              type="checkbox"
              className="hidden"
              checked={formData.status === 'Gjennomført'}
              onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'Gjennomført' : 'Planlagt' })}
            />
            <div>
              <p className="text-sm font-black text-slate-900 uppercase tracking-wider">Fullført</p>
              <p className="text-[10px] text-slate-500 font-bold">Marker som utført</p>
            </div>
          </label>

          <div className="flex gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-6 py-3 bg-white border border-slate-300 text-slate-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
            >
              Avbryt
            </button>
            <button
              type="submit"
              form="activity-form"
              disabled={isSaving}
              className="flex-1 sm:flex-none px-8 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <SafeIcon icon="💾" className="w-4 h-4" /> {isSaving ? 'Lagrer...' : 'Lagre'}
            </button>
          </div>
        </div>

      </div>

      {/* Right Column: Schedule Preview (Hidden on mobile, visible on lg) */}
      <div className="hidden lg:flex w-96 bg-slate-50 p-8 flex-col border-l border-slate-200">
        <div className="mb-8">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Dagens kalender</p>
          <h3 className="text-xl font-black text-slate-900 tracking-tight italic">
            {new Date(formData.date).toLocaleDateString('no-NO', { weekday: 'long', day: 'numeric', month: 'short' })}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
          <div className="relative border-l-2 border-slate-200 ml-4 space-y-8 py-4">
            {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(hour => {
              const acts = sameDayActivities.filter(a => new Date(a.date).getHours() === hour);
              const isCurrentHour = new Date(formData.date).getHours() === hour;

              return (
                <div key={hour} className="relative pl-8">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 bg-white border-2 border-slate-300 rounded-full z-10" />
                  <span className="absolute -left-12 top-0.5 text-[10px] font-black text-slate-500 tracking-tighter">{hour}:00</span>
                  
                  <div className="space-y-3">
                    {acts.map(a => {
                      const colors = getActivityColor(a.type);
                      return (
                        <div key={a.activity_id} className={`p-4 rounded-xl border-2 ${colors.bg} ${colors.border} ${colors.text} shadow-sm group hover:scale-[1.02] transition-transform`}>
                          <div className="font-black text-xs truncate">{a.title}</div>
                          <div className="flex items-center gap-2 mt-1 opacity-70">
                            <SafeIcon icon={getActivityIcon(a.type)} className="w-3 h-3" />
                            <span className="text-[9px] font-bold uppercase tracking-widest">
                              {new Date(a.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {isCurrentHour && (
                      <div className={`p-4 rounded-xl border-2 border-dashed ${currentColors.border} ${currentColors.bg} ${currentColors.text} opacity-80 animate-pulse shadow-sm`}>
                        <div className="font-black text-xs truncate">{formData.title || '(Ny aktivitet)'}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8 p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${currentColors.bg} ${currentColors.text} border ${currentColors.border}`}>
              <SafeIcon icon={getActivityIcon(formData.type)} className="w-4 h-4" />
            </div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest italic">Valgt type</p>
          </div>
          <p className="text-sm font-black text-slate-900">{formData.type}</p>
        </div>
      </div>

    </div>
  );
};

export default ActivityForm;